import {
  infiniteQueryOptions,
  keepPreviousData,
  queryOptions,
  useMutation,
} from "@tanstack/react-query"

import { apiKey } from "@/lib/api/changes"
import { apiFetch } from "@/lib/api/client"
import type { ApiResource } from "@/lib/api/types"
import type {
  ActivityEntry,
  ActivityFilters,
  ActivityPage,
  ActivitySubject,
} from "@/types/activity"

function subjectPath(subject: ActivitySubject): string {
  if (subject.type === "organisation") {
    return "/activity"
  }

  const resource = subject.type === "firm" ? "firms" : "individuals"

  return `/${resource}/${subject.id}/activity`
}

export const activityQueries = {
  feed: (subject: ActivitySubject, filters: ActivityFilters = {}) =>
    infiniteQueryOptions({
      queryKey: apiKey(subjectPath(subject), filters),
      queryFn: async ({ pageParam, signal }) =>
        await apiFetch<ActivityPage>(subjectPath(subject), {
          params: { ...filters, cursor: pageParam },
          signal,
        }),
      initialPageParam: undefined as string | undefined,
      getNextPageParam: (lastPage) => lastPage.meta.next_cursor ?? undefined,
    }),
}

/**
 * A title for an update while it is written. The same words always get the
 * same suggestion, so it is cached for good; the last one is kept while the
 * next is fetched.
 */
export function updateTitleQuery(subject: RecordActivitySubject, body: string) {
  return queryOptions({
    queryKey: apiKey(`/${recordPath(subject)}/comments/title`, { body }),
    queryFn: async ({ signal }) => {
      const { data } = await apiFetch<ApiResource<{ title: string | null }>>(
        `/${recordPath(subject)}/comments/title`,
        {
          method: "POST",
          body: { body },
          signal,
        }
      )

      return data.title
    },
    staleTime: Infinity,
    retry: false,
    placeholderData: keepPreviousData,
  })
}

/** A comment on the firm or person, which the API records as an activity. */
export function useShareUpdate(subject: RecordActivitySubject) {
  return useMutation({
    mutationFn: async (input: { title: string | null; body: string }) =>
      await apiFetch<ApiResource<ActivityEntry>>(
        `/${recordPath(subject)}/comments`,
        { method: "POST", body: input }
      ),
  })
}

type RecordActivitySubject = Extract<ActivitySubject, { id: string }>

function recordPath(subject: RecordActivitySubject): string {
  return `${subject.type === "firm" ? "firms" : "individuals"}/${subject.id}`
}
